import React, { useState, useEffect } from "react";
import {
  Input,
  Popover,
  Button,
  Menu,
  Dropdown,
  Space,
  Checkbox,
  Card,
  message,
} from "antd";
import { DownOutlined, DownloadOutlined } from "@ant-design/icons";
import icon_carbon from "../../assets/icons/carbonemission_icon.png";
import icon_recycle from "../../assets/icons/recycle_icon.png";
import icon_carbonreported from "../../assets/icons/carbonreported_icon.png";
import icon_goals from "../../assets/icons/goals_icon.png";
import icon_filter from "../../assets/icons/filter_icon.png";
import info_icon from "../../assets/icons/info_icon.png";
import {
  COUNTRIES,
  DASH_CARDS,
} from "../../constants/Constants";
import { TEXT_CONSTANTS } from "../../constants/Constants";
import "./AdminDashBoard.css";
import DashCardWrapper from "../../components/layout/card";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { 
  setRecyclePackaging, 
  setCarbonEmissionAvoided, 
  setCarbonReported,
  setSupplierSubmittedGoals,
  setSelectedMarkets,
  setSupplierName
} from "../../redux/slices/dashboardSlice";
import { API_BASE_URL } from "../../services/apiService";
import { downloadDashboardExcel } from "../../util/excelUtils";


const AdminDashBoard = () => {
  const [pagePermission, setPagePermission] = useState({});
  const [allDataLoaded, setAllDataLoaded] = useState(false);

  useEffect(() => {
    const permission = sessionStorage.getItem('UserPermission');
    if (permission) {
      const parsedPermissions = JSON.parse(permission);
      setPagePermission(parsedPermissions);
    }
  }, []);
  
  const dispatch = useDispatch();
  const role = sessionStorage.getItem("Role");

  const { 
    selectedMarkets, 
    supplierName,
    carbonEmissionAvoided,
    recyclePackaging,
    carbonReported,
    supplierSubmittedGoals
  } = useSelector(state => state.dashboard);
  
  const [filterActive, setFilterActive] = useState(false);

  const [recyclePackagingState, setRecyclePackagingState] = useState({
    isLoading: true,
    error: null
  });

  const [carbonEmissionState, setCarbonEmissionState] = useState({
    isLoading: true,
    error: null
  });

  const [carbonReportedState, setCarbonReportedState] = useState({
    isLoading: true,
    error: null
  });

  const [supplierGoalsState, setSupplierGoalsState] = useState({
    isLoading: true,
    error: null
  });

  // Convert country codes to names for display
  const countries = Object.entries(COUNTRIES).map(([key, name], index) => ({
    key: key,
    index: index + 1,
    name: name,
  }));

  // Handle country selection in filter
  const handleCountryChange = (countryKey) => {
    const updatedMarkets = [...selectedMarkets];
    
    if (updatedMarkets.includes(countryKey)) {
      const filteredMarkets = updatedMarkets.filter(item => item !== countryKey);
      dispatch(setSelectedMarkets(filteredMarkets));
    } else {
      updatedMarkets.push(countryKey);
      dispatch(setSelectedMarkets(updatedMarkets));
    }
  };

  const countryMenu = (
    <Menu>
      {countries.map((country) => (
        <Menu.Item key={country.key}>
          <Checkbox 
            checked={selectedMarkets.includes(country.key)}
            onChange={() => handleCountryChange(country.key)}
          >
            {country.name}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  const fetchRecyclePackagingData = async (filters = {}) => {
    try {
      const payload = {
        supplierName: filters.supplierName || "",
        marketCode: filters.markets || []
      };
      
      const response = await axios.post(
        `${API_BASE_URL}dashboard/admin/recyclePackaging`,
        payload,
        {
          headers: {
            "Authorization": `Bearer ${sessionStorage.getItem("authToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      dispatch(setRecyclePackaging(response.data));
      setRecyclePackagingState({
        isLoading: false,
        error: null
      });
      return true;
    } catch (error) {
      console.error("Error fetching recycle packaging data:", error);
      setRecyclePackagingState({
        isLoading: false,
        error: error.message || "Failed to fetch recycle packaging data"
      });
      return false;
    }
  };

  const fetchCarbonEmissionData = async (filters = {}) => {
    try {
      const payload = {
        supplierName: filters.supplierName || "",
        marketCode: filters.markets || []
      };
      
      const response = await axios.post(
        `${API_BASE_URL}dashboard/admin/carbonEmissionAvoided`,
        payload,
        {
          headers: {
            "Authorization": `Bearer ${sessionStorage.getItem("authToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      dispatch(setCarbonEmissionAvoided(response.data));
      setCarbonEmissionState({
        isLoading: false,
        error: null
      });
      return true;
    } catch (error) {
      console.error("Error fetching carbon emission data:", error);
      setCarbonEmissionState({
        isLoading: false,
        error: error.message || "Failed to fetch carbon emission data"
      });
      return false;
    }
  };

  const fetchCarbonReportedData = async (filters = {}) => {
    try {
      const payload = {
        supplierName: filters.supplierName || "",
        marketCode: filters.markets || []
      };
      
      const response = await axios.post(
        `${API_BASE_URL}dashboard/admin/cdpReported`,
        payload,
        {
          headers: {
            "Authorization": `Bearer ${sessionStorage.getItem("authToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      dispatch(setCarbonReported(response.data));
      setCarbonReportedState({
        isLoading: false,
        error: null
      });
      return true; 
    } catch (error) {
      console.error("Error fetching carbon reported data:", error);
      setCarbonReportedState({
        isLoading: false,
        error: error.message || "Failed to fetch carbon reported data"
      });
      return false;
    }
  };

  const fetchSupplierGoalsData = async (filters = {}) => {
    try {
      const payload = {
        supplierName: filters.supplierName || "",
        marketCode: filters.markets || []
      };
      
      const response = await axios.post(
        `${API_BASE_URL}dashboard/admin/supplierSubmittedGoals`,
        payload,
        {
          headers: {
            "Authorization": `Bearer ${sessionStorage.getItem("authToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      dispatch(setSupplierSubmittedGoals(response.data));
      setSupplierGoalsState({
        isLoading: false,
        error: null
      });
      return true;
    } catch (error) {
      console.error("Error fetching supplier goals data:", error);
      setSupplierGoalsState({
        isLoading: false,
        error: error.message || "Failed to fetch supplier goals data"
      });
      return false;
    }
  };

  // Fetch all data in parallel instead of sequentially
  const fetchAllData = async (filters = {}) => {
    // Reset loading states
    setRecyclePackagingState(prev => ({ ...prev, isLoading: true }));
    setCarbonEmissionState(prev => ({ ...prev, isLoading: true }));
    setCarbonReportedState(prev => ({ ...prev, isLoading: true }));
    setSupplierGoalsState(prev => ({ ...prev, isLoading: true }));
    
    // Start all API calls in parallel
    const recyclePromise = fetchRecyclePackagingData(filters)
      .catch(error => {
        console.error("Recycle packaging API failed:", error);
        setRecyclePackagingState({
          isLoading: false,
          error: error.message || "Failed to fetch recycle packaging data"
        });
        return false;
      });
    
    const carbonEmissionPromise = fetchCarbonEmissionData(filters)
      .catch(error => {
        console.error("Carbon emission API failed:", error);
        setCarbonEmissionState({
          isLoading: false,
          error: error.message || "Failed to fetch carbon emission data"
        });
        return false;
      });
    
    const carbonReportedPromise = fetchCarbonReportedData(filters)
      .catch(error => {
        console.error("Carbon reported API failed:", error);
        setCarbonReportedState({
          isLoading: false,
          error: error.message || "Failed to fetch carbon reported data"
        });
        return false;
      });
    
    const supplierGoalsPromise = fetchSupplierGoalsData(filters)
      .catch(error => {
        console.error("Supplier goals API failed:", error);
        setSupplierGoalsState({
          isLoading: false,
          error: error.message || "Failed to fetch supplier goals data"
        });
        return false;
      });
    
    await Promise.allSettled([
      recyclePromise,
      carbonEmissionPromise,
      carbonReportedPromise,
      supplierGoalsPromise
    ]);
  };


  useEffect(() => {
    const filters = {
      supplierName,
      markets: selectedMarkets
    };
    fetchAllData(filters);
  }, []);

  // Check if all data has finished loading (for download button state)
  useEffect(() => {
    if (
      !recyclePackagingState.isLoading && 
      !carbonEmissionState.isLoading && 
      !carbonReportedState.isLoading && 
      !supplierGoalsState.isLoading
    ) {
      setAllDataLoaded(true);
    } else {
      setAllDataLoaded(false);
    }
  }, [
    recyclePackagingState.isLoading,
    carbonEmissionState.isLoading,
    carbonReportedState.isLoading,
    supplierGoalsState.isLoading
  ]);

  // Handle applying filters
  const applyFilters = () => {
    const filters = {
      supplierName,
      markets: selectedMarkets
    };
    
    fetchAllData(filters);
    setFilterActive(false);
  };

  const handleDownload = () => {
    if (pagePermission["ADMIN_DASHBOARD_DOWNLOAD"]) {
      console.log("Download requested with filters:", { supplierName, markets: selectedMarkets });
      downloadDashboardExcel(recyclePackaging, carbonEmissionAvoided, carbonReported, supplierSubmittedGoals);
    } else {
      message.warning("You do not have access to download");
    }
  };

  const handleSupplierNameChange = (e) => {
    dispatch(setSupplierName(e.target.value));
  };

  const popoverContent = (
    <div className="filter-popover-content">
      <div className="filter-section">
        <Dropdown overlay={countryMenu}>
          <button className="filter-dropdown-btn">
            <Space>
              {TEXT_CONSTANTS.MARKET}
              <DownOutlined />
            </Space>
          </button>
        </Dropdown>
      </div>
      
      <div className="filter-section">
        <Input
          className="supplier-input"
          value={supplierName}
          onChange={handleSupplierNameChange}
          placeholder={TEXT_CONSTANTS.SUPPLIER_NAME_PLACEHOLDER}
        />
      </div>
      
      <div className="filter-btn-container">
        <Button 
          className="apply-filter-btn"
          onClick={applyFilters}
        >
          {TEXT_CONSTANTS.APPLY}
        </Button>
      </div>
    </div>
  );

  const handleVisibleChange = (visible) => {
    setFilterActive(visible);
  };
  
  if (pagePermission["ADMIN_DASHBOARD"] === false) {
    return <div>You do not have permission to access the Admin Dashboard.</div>;
  }

  return (
    <div data-testid="dashboard" className="w-full p-6">
      <Card className="w-full bg-white relative mb-[20px] h-auto">
        <div className="flex items-center justify-between flex-col md:flex-row lg:flex-row gap-2">
          <div className="flex flex-col justify-start self-start">
            <span className="text-[18px] font-bold text-[#2C5778] m-0">
              {TEXT_CONSTANTS.DASHBOARD_TITLE}
            </span>
            <span className="text-[10px] font-bold text-[#2C5778] m-0">
              {TEXT_CONSTANTS.KPI_SUMMARY}
            </span>
          </div>
         
          <div className="flex justify-between md:justify-end items-center w-full md:w-[250px]">
            <Button
              type="primary"
              shape="round"
              disabled={!allDataLoaded}
              icon={<DownloadOutlined />}
              size="60px"
              className="top-[3px] w-[175px] h-[43px] bg-[#1F405A]"
              onClick={handleDownload}
            >
              {TEXT_CONSTANTS.DOWNLOAD_BUTTON}
            </Button>
            <Popover
              content={popoverContent}
              trigger="click"
              placement="bottomRight"
              className="md:ml-[10px]"
              overlayClassName="filter-popover"
              open={filterActive}
              onOpenChange={handleVisibleChange}
            >
           <div className={filterActive ? "filter-icon-active" : ""}>
              <img
                src={icon_filter}
                alt="Filter Icon"
                className="h-6 w-6 cursor-pointer"
              />
           </div>
            </Popover>
          </div>
        </div>
      </Card>
      {/* Dashboard Cards Container */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        <DashCardWrapper
          icon={icon_carbon}
          title={DASH_CARDS.CARBON_EMISSION_AVOIDED.TITLE}
          infoicon={info_icon}
          fields={DASH_CARDS.CARBON_EMISSION_AVOIDED.FIELDS}
          carbonEmissionData={carbonEmissionAvoided}
          isLoading={carbonEmissionState.isLoading}
          error={carbonEmissionState.error}
          role={role}
        />

        <DashCardWrapper
          icon={icon_recycle}
          title={DASH_CARDS.RECYCLE_PACKAGING.TITLE}
          infoicon={info_icon}
          fields={DASH_CARDS.RECYCLE_PACKAGING.FIELDS}
          recycleData={recyclePackaging}
          isLoading={recyclePackagingState.isLoading}
          error={recyclePackagingState.error}
          role={role}
        />

        <DashCardWrapper
          icon={icon_carbonreported}
          title={DASH_CARDS.CARBON_REPORTED.TITLE}
          infoicon={info_icon}
          fields={DASH_CARDS.CARBON_REPORTED.FIELDS}
          carbonReportedData={carbonReported}
          isLoading={carbonReportedState.isLoading}
          error={carbonReportedState.error}
          role={role}
        />

        <DashCardWrapper
          icon={icon_goals}
          title={DASH_CARDS.SUPPLIERS_SUBMITTED_GOALS.TITLE}
          infoicon={info_icon}
          fields={DASH_CARDS.SUPPLIERS_SUBMITTED_GOALS.FIELDS}
          supplierSubmittedGoalsData={supplierSubmittedGoals}
          isLoading={supplierGoalsState.isLoading}
          error={supplierGoalsState.error}
          role={role}
        />
      </div>
    </div>
  );
};

export default AdminDashBoard;